import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { changeChannelActionSilent, setChannelNameAction } from './EditChannel/channelActions';
import { extractChannelNumberFromId } from '../../../utils/channel-utils';
import SpinnerLoader from '../../../components/SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';
import { DEFAULT_INPUT_CLASS } from '../../../components/InputControl/constants';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../constants/common';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

// Splits a block of pasted text (e.g. a column copied from a spreadsheet) into one
// value per line. If a line itself has tab-separated cells (a multi-column paste),
// the last cell is used, so pasting either a single "Name" column or a
// "Channel<TAB>Name" range both land the name in the right place.
const splitPastedNames = (pasted: string): string[] => {
  const lines = pasted.split(/\r\n|\r|\n/);

  while (lines.length && lines[lines.length - 1] === '') {
    lines.pop();
  }

  return lines.map((line) => {
    const cells = line.split('\t');
    return cells[cells.length - 1].trim();
  });
};

const ChannelsNames: React.FC<ComponentProps> = ({ getChannels, channels }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draftNames, setDraftNames] = useState<Record<string, string>>({});
  const [dirtyIds, setDirtyIds] = useState<Record<string, true>>({});

  const fetchData = async (controllerId: string) => {
    setIsLoading(true);
    await getChannels(controllerId);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData(id);
    const intervalId = setInterval(() => fetchData(id), EXTENDED_INTERVAL_UPDATE_DATA);

    return () => clearInterval(intervalId);
  }, [id]);

  // Keep local drafts in sync with the server, but never clobber a row the user is
  // actively editing (or has unsaved changes for).
  useEffect(() => {
    setDraftNames((prev) => {
      const next = { ...prev };
      channels?.forEach((item) => {
        if (!dirtyIds[item.id]) {
          next[item.id] = item.name;
        }
      });
      return next;
    });
  }, [channels, dirtyIds]);

  const setName = (channelId: string, value: string) => {
    setDraftNames((prev) => ({ ...prev, [channelId]: value }));
    setDirtyIds((prev) => ({ ...prev, [channelId]: true }));
  };

  const onChangeHandler = (channelId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(channelId, event.target.value);
  };

  const onPasteHandler = (rowIndex: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');

    if (!/\r|\n/.test(pasted)) {
      // Single value paste — let the browser's default paste behaviour handle it.
      return;
    }

    event.preventDefault();

    const values = splitPastedNames(pasted);

    values.forEach((value, offset) => {
      const channel = channels?.[rowIndex + offset];
      if (channel) {
        setName(channel.id, value);
      }
    });
  };

  const changedChannels = useMemo(
    () => (channels ?? []).filter((item) => dirtyIds[item.id] && draftNames[item.id] !== item.name),
    [channels, dirtyIds, draftNames],
  );

  const saveChannelNames = async (updates: { id: string; name: string }[]) => {
    if (!updates.length) return;

    setIsSaving(true);

    const results = await Promise.all(
      updates.map(({ id: channelId, name }) =>
        changeChannelActionSilent(id, extractChannelNumberFromId(channelId), setChannelNameAction.actionName, name),
      ),
    );

    setIsSaving(false);

    const succeededIds = updates.filter((_, idx) => results[idx]).map((item) => item.id);
    const failedCount = results.length - succeededIds.length;

    if (succeededIds.length) {
      // Don't clear the dirty flag here: a successful save only means the
      // rename command was accepted, not that the controller has reported
      // the new name back yet. The fetchData() below can still return the
      // old name for a moment - keeping the row dirty stops that stale
      // response from clobbering what the user just typed. changedChannels
      // already stops counting a row as changed once the server value
      // catches up to the draft, so this resolves itself automatically.
      toast.success(`${succeededIds.length} channel name${succeededIds.length > 1 ? 's' : ''} updated successfully`);
    }

    if (failedCount) {
      toast.error(`${failedCount} channel name${failedCount > 1 ? 's' : ''} failed to update`);
    }

    await fetchData(id);
  };

  const onClearAllHandler = async () => {
    if (!channels?.length) return;

    channels.forEach((item) => setName(item.id, ' '));

    await saveChannelNames(channels.map((item) => ({ id: item.id, name: ' ' })));
  };

  const onSaveAllHandler = () => saveChannelNames(changedChannels.map((item) => ({ id: item.id, name: draftNames[item.id] })));

  if (!channels?.length && isLoading) {
    return (
      <div className="p-edit-controller-channel-names pb-4 md:pb-25">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex h-full w-full items-center justify-center py-10">
            <SpinnerLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-edit-controller-channel-names pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="flex items-center justify-between border-b border-stroke px-7 py-4 dark:border-strokedark">
          <div>
            <h3 className="font-medium text-black dark:text-white">Edit Channels Names</h3>
            <p className="mt-1 text-sm text-slate-gray">
              Tip: copy a column of names from a spreadsheet and paste it into any row below to fill the rest
              automatically.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <InteractiveButton
              text={isSaving ? 'Saving...' : 'Clear All'}
              type={ButtonType.PrimaryInverse}
              onClick={onClearAllHandler}
              isLoading={isSaving}
              disabled={isSaving || !channels?.length}
            />
            <InteractiveButton
              text={isSaving ? 'Saving...' : 'Update All'}
              onClick={onSaveAllHandler}
              isLoading={isSaving}
              disabled={!changedChannels.length}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-primary text-left text-xs uppercase leading-6 text-white">
                <th className="w-[120px] py-2 pl-7">Channel</th>
                <th className="w-1/3 py-2 pr-2">Current Name</th>
                <th className="py-2 pr-7">New Name</th>
              </tr>
            </thead>
            <tbody>
              {channels?.map((item, idx) => (
                <tr
                  key={item.id}
                  className="border-b border-stroke odd:bg-white even:bg-gray-2 hover:bg-stroke dark:border-strokedark"
                >
                  <td className="py-2 pl-7 font-medium text-black dark:text-white">{item.channel}</td>
                  <td className="py-2 pr-2 text-slate-gray">{item.name || '-'}</td>
                  <td className="py-2 pr-7">
                    <input
                      type="text"
                      className={`${DEFAULT_INPUT_CLASS} !border-2 !border-black dark:!border-black`}
                      value={draftNames[item.id] ?? ''}
                      onChange={onChangeHandler(item.id)}
                      onPaste={onPasteHandler(idx)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  channels: state.channels.channels,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannels: dispatch.channels.getChannels,
});

export default connect(mapState, mapDispatch)(ChannelsNames);
